// src/features/static-pages/Home.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Sliders, Cpu, ShieldCheck, BrainCircuit, Store, Rocket } from 'lucide-react';
import NeuralNetworkHerov2 from '../../components/hero/NeuralNetworkHerov2';
import rgbnnSmallImage from '../../assets/RGBNN(small).jpg';

const Home = () => {
  const neuropolStyle = {
    fontFamily: 'Neuropol, sans-serif'
  };

  return (
    <>
      <Helmet>
        <title>ContextUI - Build & Run Open Source Tools. All Local and Free.</title>
        <meta name="description" content="ContextUI makes powerful open-source tools simple, beautiful, and accessible to everyone. A local-first workspace where you can build, share, and run AI workflows and productivity tools — no coding required." />
        <meta property="og:title" content="ContextUI - Build & Run Open Source Tools. All Local and Free." />
        <meta property="og:description" content="ContextUI makes powerful open-source tools simple, beautiful, and accessible to everyone. A local-first workspace where you can build, share, and run AI workflows and productivity tools — no coding required." />
        <meta property="og:url" content="https://contextui.ai/" />
        <link rel="canonical" href="https://contextui.ai/" />
      </Helmet>

      <div className="bg-black min-h-screen flex flex-col">
      {/* Neural Network Animation Hero for desktop, Hidden on mobile */}
      <div className="hidden md:block relative">
        <NeuralNetworkHerov2 />
        
        {/* Hero Content Overlay - Desktop */}
        <div className="absolute inset-0 z-10 flex items-center justify-center px-6">
          <div className="max-w-4xl mx-auto text-center bg-black/40 backdrop-blur-sm rounded-2xl p-12">
            <h1 className="text-5xl md:text-7xl font-bold mb-4 bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent" style={neuropolStyle}>
              ContextUI
            </h1>

            <p className="text-2xl md:text-3xl text-gray-400 mb-12">
              The Do-Any-Task Workspace. 
            </p>

            <Link
              to="/download"
              className="inline-block px-8 py-4 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-lg
                      font-semibold text-lg hover:from-emerald-600 hover:to-teal-600 transition-all">
              Get Started Free
            </Link>
          </div>
        </div>
      </div>

      {/* Static Image Hero for mobile only */}
      <div className="relative w-full h-[550px] block md:hidden">
        {/* Static image background for mobile - using small image version */}
        <div className="absolute inset-0">
          <img 
            src={rgbnnSmallImage} 
            alt="Neural Network Visualization" 
            className="w-full h-full object-cover" 
            style={{ opacity: 0.7 }}
          />
          <div className="absolute inset-0 bg-black bg-opacity-40"></div>
        </div>
        
        {/* Content overlay - mobile */}
        <div className="absolute inset-0 z-10 flex items-center justify-center px-6">
          <div className="text-center bg-black/40 backdrop-blur-sm rounded-xl p-8">
            <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent" style={neuropolStyle}>
              ContextUI
            </h1>

            <p className="text-xl text-gray-400 mb-8">
              If you can describe it, you can build it
            </p>

            <Link
              to="/download"
              className="inline-block px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-lg
                      font-semibold hover:from-emerald-600 hover:to-teal-600 transition-all">
              Get Started Free
            </Link>
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div className="py-24 px-6 border-t border-gray-800 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-16 text-center">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-8">

            <div className="bg-gradient-to-br from-blue-900/20 to-gray-900/50 p-8 rounded-xl border border-blue-700/30 hover:border-blue-500/50 transition-all">
              <Sliders className="w-12 h-12 text-blue-400 mb-6" />
              <h3 className="text-xl font-bold text-white mb-3">
                Software That Adapts to You
              </h3>
              <p className="text-gray-300 leading-relaxed">
                Build custom tools that fit your workflow — not the other way around.
              </p>
            </div>

            <div className="bg-gradient-to-br from-blue-800/20 to-gray-900/50 p-8 rounded-xl border border-blue-600/30 hover:border-blue-400/50 transition-all">
              <Cpu className="w-12 h-12 text-blue-300 mb-6" />
              <h3 className="text-xl font-bold text-white mb-3">
                Powerful Tools, Made Usable
              </h3>
              <p className="text-gray-300 leading-relaxed">
                AI models, Python code, media processing, file automation, custom APIs — all through a visual interface instead of command lines.
              </p>
            </div>

            <div className="bg-gradient-to-br from-indigo-900/20 to-gray-900/50 p-8 rounded-xl border border-indigo-700/30 hover:border-indigo-500/50 transition-all">
              <ShieldCheck className="w-12 h-12 text-indigo-400 mb-6" />
              <h3 className="text-xl font-bold text-white mb-3">
                Local-First, Private by Default
              </h3>
              <p className="text-gray-300 leading-relaxed">
                Runs on your machine. Full file system access, GPU support, no cloud dependency. Your data never leaves.
              </p>
            </div>

            <div className="bg-gradient-to-br from-indigo-800/20 to-gray-900/50 p-8 rounded-xl border border-indigo-600/30 hover:border-indigo-400/50 transition-all">
              <BrainCircuit className="w-12 h-12 text-indigo-300 mb-6" />
              <h3 className="text-xl font-bold text-white mb-3">
                AI That Runs Workflows
              </h3>
              <p className="text-gray-300 leading-relaxed">
                Not just code generation. AI executes step by step, tests outcomes, and refines results until it works.
              </p>
            </div>

            <div className="bg-gradient-to-br from-purple-900/20 to-gray-900/50 p-8 rounded-xl border border-purple-700/30 hover:border-purple-500/50 transition-all">
              <Store className="w-12 h-12 text-purple-400 mb-6" />
              <h3 className="text-xl font-bold text-white mb-3">
                The Exchange
              </h3>
              <p className="text-gray-300 leading-relaxed">
                Share what you build. Sell it, give it away, or keep it private. Creators earn 70% of every sale.
              </p>
            </div>

            <div className="bg-gradient-to-br from-purple-800/20 to-gray-900/50 p-8 rounded-xl border border-purple-600/30 hover:border-purple-400/50 transition-all">
              <Rocket className="w-12 h-12 text-purple-300 mb-6" />
              <h3 className="text-xl font-bold text-white mb-3">
                Build It. Run It. Share It. Own It.
              </h3>
              <p className="text-gray-300 leading-relaxed">
                From idea to tool in hours. No setup, no deployment, no boilerplate.
              </p>
            </div>

          </div>
        </div>
      </div>


      {/* CTA Section */}
      <div className="py-24 px-6 border-t border-gray-800">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Get Started
          </h2>
          <p className="text-xl md:text-2xl text-gray-300 mb-10">
            Free. Full-featured. Windows & Linux.
          </p>
          <Link
            to="/download"
            className="inline-block px-12 py-5 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg
                    font-bold text-xl hover:from-blue-700 hover:to-indigo-700 transition-all shadow-2xl hover:shadow-indigo-500/50">
            Download ContextUI
          </Link>
        </div>
      </div>
    </div>
    </>
  );
};

export default Home;